import React, { useState, useEffect } from 'react';
import AdminChatbotPreview from './AdminChatbotPreview';
import {
  Card,
  TextField,
  Select,
  Button,
  DropZone,
  ColorPicker,
  FormLayout,
  Toast,
  Frame,
  InlineStack,
  Text,
  Checkbox
} from '@shopify/polaris';
import Chatbot from '../chatbot/Chatbot.jsx';
import { plans, getResolvedFeatures } from '../plans';
import { config } from '../constants';

const API_BASE = config.API_BASE;


const positionOptions = [
  {label: 'Bottom Right', value: 'bottom-right'},
  {label: 'Bottom Left', value: 'bottom-left'},
];
const shapeOptions = [
  {label: 'Rounded', value: 'rounded'},
  {label: 'Square', value: 'square'},
  {label: 'Pill', value: 'pill'},
];

export default function ChatboxCustomizationCard() {
  // Fetch customization settings on mount
  // Fetch customization settings from backend
  async function fetchCustomization() {
    const params = new URLSearchParams(window.location.search);
    const shopDomain = params.get('shop');
    if (!shopDomain) return;
    try {
      const res = await fetch(`${API_BASE}/customization?shop=${shopDomain}`);
      if (!res.ok) return;
      const data = await res.json();
      // Defensive: Only set state if value exists in response
      if (typeof data.chatboxEnabled === 'boolean') setChatboxEnabled(data.chatboxEnabled);
      if (typeof data.logoUrl === 'string') setLogoUrl(data.logoUrl);
      if (typeof data.header === 'string') setHeader(data.header);
      
      if (typeof data.botName === 'string') setBotName(data.botName);
      if (typeof data.position === 'string') setPosition(data.position);
      if (typeof data.shape === 'string') setShape(data.shape);
      if (typeof data.headerBgColor === 'string') setHeaderBgColor(data.headerBgColor);
      if (typeof data.chatBgColor === 'string') setChatBgColor(data.chatBgColor);
      if (typeof data.footerBgColor === 'string') setFooterBgColor(data.footerBgColor);
      if (typeof data.chatInputBgColor === 'string') setChatInputBgColor(data.chatInputBgColor);
      if (typeof data.fontColor === 'string') setFontColor(data.fontColor);
      if (typeof data.quickQuestionPillBgColor === 'string') setQuickQuestionPillBgColor(data.quickQuestionPillBgColor);
      if (typeof data.welcomeMessage === 'string') setWelcomeMessage(data.welcomeMessage);
      if (typeof data.botStatusText === 'string') setBotStatusText(data.botStatusText);
      if (typeof data.liveChatEnabled === 'boolean') setLiveChatEnabled(data.liveChatEnabled);
      if (typeof data.liveChatMessage === 'string') setLiveChatMessage(data.liveChatMessage);
      if (typeof data.chatBubbleOpenColor === 'string') setChatBubbleOpenColor(data.chatBubbleOpenColor);
      if (typeof data.chatBubbleClosedColor === 'string') setChatBubbleClosedColor(data.chatBubbleClosedColor);
      let qv = data.quickQuestionsVisibility;
      if (typeof qv === 'string') {
        try {
          qv = JSON.parse(qv);
        } catch (e) {
          qv = null;
        }
      }
      if (qv && typeof qv === 'object') setQuickQuestionsVisibility(qv);
    } catch (err) {
      // Optionally, show error toast or log
    }
  }

  useEffect(() => {
    fetchCustomization();
  }, []);
  // --- Font Color State ---
  const [fontColor, setFontColor] = useState('#ffffff');
  const [lastValidFontHex, setLastValidFontHex] = useState('#ffffff');
  const [fontHSB, setFontHSB] = useState(hexToHsb('#ffffff')); // HSB state for picker
  // --- Bot Response Bubble Color ---
  const [botBubbleColor, setBotBubbleColor] = useState('#333333');
  const [lastValidBotBubbleHex, setLastValidBotBubbleHex] = useState('#333333');
  const [botBubbleHSB, setBotBubbleHSB] = useState(hexToHsb('#333333')); // HSB state for picker
  // --- User Message Bubble Color ---
  const [userBubbleColor, setUserBubbleColor] = useState('#0B93F6');
  const [lastValidUserBubbleHex, setLastValidUserBubbleHex] = useState('#0B93F6');
  const [userBubbleHSB, setUserBubbleHSB] = useState(hexToHsb('#0B93F6'));
  // --- Feature/Plan State ---
  const [features, setFeatures] = useState([]);
  const [plan, setPlan] = useState('Free');
  const [loadingFeatures, setLoadingFeatures] = useState(true);

  // Get shop domain from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shopDomain = params.get('shop');
    if (!shopDomain) return;
    async function fetchPlan() {
      setLoadingFeatures(true);
      try {
        const res = await fetch(`${API_BASE}/current-plan?shop=${shopDomain}`);
        const data = await res.json();
        setPlan(data?.plan || 'Free');
        setFeatures(getResolvedFeatures(data?.plan || 'Free', plans));
      } catch {
        setPlan('Free');
        setFeatures(getResolvedFeatures('Free', plans));
      } finally {
        setLoadingFeatures(false);
      }
    }
    fetchPlan();
  }, []);

  const [chatboxEnabled, setChatboxEnabled] = useState(true);
  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [header, setHeader] = useState('Hi 👋');
  const [welcomeMessage, setWelcomeMessage] = useState('Hi! What can I help you with?');
  const [botStatusText, setBotStatusText] = useState('You are talking to a bot');
  // --- Color Picker State Initialization ---
  // Reverted to original defaults (mostly black/gray)
  const [headerBgColor, setHeaderBgColor] = useState('linear-gradient(90deg, #232526 0%, #414345 100%)');
  const [lastValidHeaderHex, setLastValidHeaderHex] = useState('#232526');
  const [headerHSB, setHeaderHSB] = useState(hexToHsb('#232526')); // HSB state for picker

  const [chatBgColor, setChatBgColor] = useState('#000000');
  const [lastValidChatHex, setLastValidChatHex] = useState('#000000');
  const [chatHSB, setChatHSB] = useState(hexToHsb('#000000')); // HSB state for picker

  const [footerBgColor, setFooterBgColor] = useState('#000000');
  const [lastValidFooterHex, setLastValidFooterHex] = useState('#000000');
  const [footerHSB, setFooterHSB] = useState(hexToHsb('#000000')); // HSB state for picker

  const [chatInputBgColor, setChatInputBgColor] = useState('#333333');
  const [lastValidChatInputHex, setLastValidChatInputHex] = useState('#333333');
  const [chatInputHSB, setChatInputHSB] = useState(hexToHsb('#333333')); // HSB state for picker

  // Define the possible quick questions and their required features (if any)
  const allQuickQuestions = [
    {
      key: 'product_recommendations',
      label: '✨ Show me product recommendations',
      feature: 'General product recommendations',
    },
    {
      key: 'order_tracking',
      label: '📦 How do I track my order?',
      feature: 'Order tracking',
    },
    {
      key: 'shipping_times',
      label: '🚚 What are your shipping times?',
      feature: null,
    },
    {
      key: 'contact_support',
      label: '💬 How can I contact support?',
      feature: null,
    },
    {
      key: 'return_policy',
      label: '🔄 What is your return policy?',
      feature: null,
    },
  ];

  // --- Chat Bubble Color Customization (Open/Closed) ---
  const [chatBubbleOpenColor, setChatBubbleOpenColor] = useState('#2D2D2D');
  const [chatBubbleClosedColor, setChatBubbleClosedColor] = useState('#2D2D2D');
  const [lastValidBubbleOpenHex, setLastValidBubbleOpenHex] = useState('#2D2D2D');
  const [lastValidBubbleClosedHex, setLastValidBubbleClosedHex] = useState('#2D2D2D');

  // HSB states for color pickers
  const [bubbleOpenHSB, setBubbleOpenHSB] = useState(hexToHsb('#2D2D2D'));
  const [bubbleClosedHSB, setBubbleClosedHSB] = useState(hexToHsb('#2D2D2D'));
  const [quickQuestionPillBgColor, setQuickQuestionPillBgColor] = useState('#000000');
  const [lastValidQuickQuestionPillHex, setLastValidQuickQuestionPillHex] = useState('#000000');
  const [quickQuestionPillHSB, setQuickQuestionPillHSB] = useState(hexToHsb('#000000'));
  // Add HSB for others as needed (headerBgColor, chatInputBgColor, etc.)


  function isHexColor(val) {
    return typeof val === 'string' && /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.test(val);
  }

  
  const [botName, setBotName] = useState('ShopBot');
  const [position, setPosition] = useState('bottom-right');
  const [shape, setShape] = useState('rounded');
  const [toastActive, setToastActive] = useState(false);
  const [toastError, setToastError] = useState('');
  // --- Live Chat Button State ---
  const [liveChatEnabled, setLiveChatEnabled] = useState(true);
  // --- Chat Input Box Color State ---


  // Only show questions for which the feature is present (or if feature is null)
  const availableQuickQuestions = allQuickQuestions.filter(q => !q.feature || features.includes(q.feature));
  // Visibility state for each quick question (default: all visible)
  const [quickQuestionsVisibility, setQuickQuestionsVisibility] = useState(() => {
    const initial = {};
    allQuickQuestions.forEach(q => { initial[q.key] = true; });
    return initial;
  });
  // Handles checkbox toggle
  function handleQuickQuestionToggle(key) {
    setQuickQuestionsVisibility(prev => ({ ...prev, [key]: !prev[key] }));
  }

  // --- Live Chat State ---
  const [liveChatMessage, setLiveChatMessage] = useState('Talk to an Agent');

  // Handle logo upload (simulate upload for now)
  const handleDrop = (files, acceptedFiles, rejectedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = () => setLogoUrl(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setToastActive(false);
    setToastError("");
    const params = new URLSearchParams(window.location.search);
    const shopDomain = params.get("shop");
    if (!shopDomain) {
      setToastError("Missing shop domain");
      setToastActive(true);
      return;
    }
    const payload = {
      shop: shopDomain,
      chatboxEnabled,
      chatBubbleOpenColor,
      chatBubbleClosedColor,
      botBubbleColor,
      userBubbleColor,
      logoUrl,
      header,
      fontColor: typeof fontColor === 'string' ? fontColor : toHex(fontColor),
      botName,
      position,
      shape,
      headerBgColor: typeof headerBgColor === 'string' ? headerBgColor : toHex(headerBgColor),
      chatBgColor: typeof chatBgColor === 'string' ? chatBgColor : toHex(chatBgColor),
      footerBgColor: typeof footerBgColor === 'string' ? footerBgColor : toHex(footerBgColor),
      chatInputBgColor: typeof chatInputBgColor === 'string' ? chatInputBgColor : toHex(chatInputBgColor),
      quickQuestionPillBgColor: typeof quickQuestionPillBgColor === 'string' ? quickQuestionPillBgColor : toHex(quickQuestionPillBgColor),
      welcomeMessage,
      botStatusText,
      liveChatEnabled,
      liveChatMessage,
      quickQuestionsVisibility,
    };
    try {
      const res = await fetch(`${API_BASE}/customization`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to save settings");
      setToastActive(true);
      setToastError("");
    } catch (err) {
      setToastActive(true);
      setToastError("Failed to save settings: " + err.message);
    }
  };
  const handleResetToDefault = () => {
    setLogoFile(null);
    setLogoUrl('');
    setHeader('Hi 👋');
    setFont('Inter');
    setFontSize('16px');
    setBotName('ShopBot');
    setPosition('bottom-right');
    setShape('rounded');
    setChatboxEnabled(true);
    setHeaderBgColor('linear-gradient(90deg, #232526 0%, #414345 100%)');
    setLastValidHeaderHex('#232526');
    setHeaderHSB(hexToHsb('#232526'));
    setChatBgColor('#000000');
    setLastValidChatHex('#000000');
    setChatHSB(hexToHsb('#000000'));
    setFooterBgColor('#000000');
    setLastValidFooterHex('#000000');
    setFooterHSB(hexToHsb('#000000'));
    setChatInputBgColor('#333333');
    setLastValidChatInputHex('#333333');
    setChatInputHSB(hexToHsb('#333333'));
    setFontColor('#ffffff');
    setLastValidFontHex('#ffffff');
    setFontHSB(hexToHsb('#ffffff'));
    setBotBubbleColor('#333333');
    setLastValidBotBubbleHex('#333333');
    setBotBubbleHSB(hexToHsb('#333333'));
    setUserBubbleColor('#0B93F6');
    setLastValidUserBubbleHex('#0B93F6');
    setUserBubbleHSB(hexToHsb('#0B93F6'));
    setQuickQuestionPillBgColor('#333333');
    setBubbleOpenHSB(hexToHsb('#000000'));
    setBubbleClosedHSB(hexToHsb('#000000'));
    setLastValidBubbleOpenHex('#000000');
    setLastValidBubbleClosedHex('#000000');
    setLastValidQuickQuestionPillHex('#333333');
    setQuickQuestionPillHSB(hexToHsb('#333333'));
    setChatBubbleOpenColor('#333333');
    setChatBubbleClosedColor('#000000');
    setWelcomeMessage('Hi! What can I help you with?');
    setBotStatusText('You are talking to a bot');
    setLiveChatEnabled(true);
    setLiveChatMessage('Talk to an Agent');
    setQuickQuestionsVisibility(() => {
      const initial = {};
      allQuickQuestions.forEach(q => { initial[q.key] = true; });
      return initial;
    });
  };

  return (
    <Frame>
      <Card sectioned>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 500, marginBottom: 8 }}>
          <Checkbox
            label="Display chatbox on your store"
            checked={chatboxEnabled}
            onChange={setChatboxEnabled}
            id="display-chatbox-checkbox"
            style={{ marginBottom: 0 }}
          />
        </div>
      </Card>
      {!chatboxEnabled && (
        <div style={{ marginTop: 10, marginBottom: 10 }}>
          <Button variant='primary' onClick={handleSave}>Save</Button>
        </div>
      )}
      <div style={{ height: 24 }} />

      {/* Quick Questions Card */}
      {chatboxEnabled && (
      <Card sectioned title="Quick Questions" style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 8, color: '#666', fontSize: 13 }}>
          Select which quick questions are visible to users in the chat widget.
        </div>
        {loadingFeatures ? (
          <div>Loading...</div>
        ) : (
          <div>
            {availableQuickQuestions.map(q => (
              <div key={q.key} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <Checkbox
                  checked={!!quickQuestionsVisibility[q.key]}
                  onChange={() => handleQuickQuestionToggle(q.key)}
                  label={q.label}
                />
              </div>
            ))}
            {availableQuickQuestions.length === 0 && (
              <div style={{ color: '#888', fontSize: 14 }}>No quick questions available for your current plan.</div>
            )}
          </div>
        )}
      </Card>      
      )}
      <div style={{ height: 24 }} />

      {/* Live Chat Card */}
      {chatboxEnabled && features.includes('Live chat') && (
        <Card sectioned title="Live Chat" style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 8, color: '#666', fontSize: 13 }}>
            Customize the message shown to users for live chat support.
          </div>
          <Checkbox
            label="Enable live chat button"
            checked={liveChatEnabled}
            onChange={setLiveChatEnabled}
            helpText="Show or hide the live chat button in the chat widget preview."
            style={{ marginBottom: 12 }}
          />
          <TextField
            label="Talk to an Agent Message"
            value={liveChatMessage}
            onChange={val => setLiveChatMessage(val)}
            autoComplete="off"
            helpText="This message will appear as the live chat prompt."
          />
        </Card>
      )}
      <div style={{ height: 24 }} />

      {chatboxEnabled && (
        <Card title="Chatbot Customization" sectioned>

        <div style={{ display: 'flex', gap: 32, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 340 }}>
            <Card sectioned>
              <FormLayout>
                <DropZone
                  label="Logo"
                  onDrop={handleDrop}
                  accept="image/*"
                  type="image"
                >
                  {logoUrl ? (
                    <img src={logoUrl} alt="Logo preview" style={{ width: 56, height: 56, borderRadius: shape === 'rounded' ? 12 : shape === 'pill' ? 28 : 0, margin: 8 }} />
                  ) : (
                    <DropZone.FileUpload actionTitle="Upload logo" />
                  )}
                </DropZone>
                <TextField label="Header" value={header} onChange={setHeader} autoComplete="off" />

                
                <FormLayout.Group>
                  <Select label="Widget Position" options={positionOptions} value={position} onChange={setPosition} />
                </FormLayout.Group>
                <TextField
                  label="Welcome Message"
                  value={welcomeMessage}
                  onChange={val => setWelcomeMessage(val)}
                  autoComplete="off"
                  helpText="The first message users see from the bot"
                />
                <TextField
                  label="Bot Status Text"
                  value={botStatusText}
                  onChange={val => setBotStatusText(val)}
                  autoComplete="off"
                  helpText="Status shown when bot is active"
                />
                <div style={{ fontWeight: 600, fontSize: 15, margin: '24px 0 8px 0' }}>Enter a color (hex, rgb, or gradient) or pick below</div>
<div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap', width: '100%' }}>
                  <div style={{ minWidth: 220, flex: '1 1 220px', marginBottom: 24 }}>
                    <div style={{ color: '#6d7175', fontSize: 13, marginBottom: 4 }}>Header Background</div>
                    
<ColorPicker
                      color={headerHSB}
                      onChange={hsb => {
                        setHeaderHSB(hsb);
                        const hex = hsbToHex(hsb);
                        setHeaderBgColor(hex);
                        setLastValidHeaderHex(hex);
                      }}
                      allowAlpha={false}
                    />
                    <div style={{ marginTop: 12 }} />
                    <TextField
                      label=""
                      value={headerBgColor}
                      onChange={val => {
                        setHeaderBgColor(val);
                        if (isHexColor(val)) {
                          setLastValidHeaderHex(val);
                          setHeaderHSB(hexToHsb(val));
                        }
                      }}
                      autoComplete="off"
                      placeholder="#232526 or CSS gradient"
                    />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Background color of the chat header.</div>
                  </div>
                  <div style={{ minWidth: 220, flex: '1 1 220px', marginBottom: 24 }}>
                    <div style={{ color: '#6d7175', fontSize: 13, marginBottom: 4 }}>Chat Input Box Color</div>
                    
<ColorPicker
                      color={chatInputHSB}
                      onChange={hsb => {
                        setChatInputHSB(hsb);
                        const hex = hsbToHex(hsb);
                        setChatInputBgColor(hex);
                        setLastValidChatInputHex(hex);
                      }}
                      allowAlpha={false}
                    />
                    <div style={{ marginTop: 12 }} />
                    <TextField
                      label=""
                      value={chatInputBgColor}
                      onChange={val => {
                        setChatInputBgColor(val);
                        if (isHexColor(val)) {
                          setLastValidChatInputHex(val);
                          setChatInputHSB(hexToHsb(val));
                        }
                      }}
                      autoComplete="off"
                      placeholder="#333333"
                    />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Background color of the chat input.</div>
                  </div>
                  <div style={{ minWidth: 220, flex: '1 1 220px', marginBottom: 24 }}>
                    <div style={{ color: '#6d7175', fontSize: 13, marginBottom: 4 }}>Chat Background Color</div>
                    
<ColorPicker
                      color={chatHSB}
                      onChange={hsb => {
                        setChatHSB(hsb);
                        const hex = hsbToHex(hsb);
                        setChatBgColor(hex);
                        setLastValidChatHex(hex);
                      }}
                      allowAlpha={false}
                    />
                    <div style={{ marginTop: 12 }} />
                    <TextField
                      label=""
                      value={chatBgColor}
                      onChange={val => {
                        setChatBgColor(val);
                        if (isHexColor(val)) {
                          setLastValidChatHex(val);
                          setChatHSB(hexToHsb(val));
                        }
                      }}
                      autoComplete="off"
                      placeholder="#333333"
                    />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Background color of the chat area.</div>
                  </div>
                  <div style={{ minWidth: 220, flex: '1 1 220px', marginBottom: 24 }}>
                    <div style={{ color: '#6d7175', fontSize: 13, marginBottom: 4 }}>Bot Message Background</div>
                    <ColorPicker
                      color={botBubbleHSB}
                      onChange={hsb => {
                        setBotBubbleHSB(hsb);
                        const hex = hsbToHex(hsb);
                        setBotBubbleColor(hex);
                        setLastValidBotBubbleHex(hex);
                      }}
                      allowAlpha={false}
                    />
                    <div style={{ marginTop: 12 }} />
                    <TextField
                      label=""
                      value={botBubbleColor}
                      onChange={val => {
                        setBotBubbleColor(val);
                        if (isHexColor(val)) {
                          setLastValidBotBubbleHex(val);
                          setBotBubbleHSB(hexToHsb(val));
                        }
                      }}
                      autoComplete="off"
                      placeholder="#333333"
                    />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Background color of bot message bubbles.</div>
                  </div>
                  <div style={{ minWidth: 220, flex: '1 1 220px', marginBottom: 24 }}>
                    <div style={{ color: '#6d7175', fontSize: 13, marginBottom: 4 }}>User Message Background</div>
                    <ColorPicker
                      color={hexToHsb(userBubbleColor)}
                      onChange={hsb => {
                        const hex = hsbToHex(hsb);
                        setUserBubbleColor(hex);
                      }}
                      allowAlpha={false}
                    />
                    <div style={{ marginTop: 12 }} />
                    <TextField
                      label=""
                      value={userBubbleColor}
                      onChange={val => {
                        setUserBubbleColor(val);
                      }}
                      autoComplete="off"
                      placeholder="#0B93F6"
                    />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Background color of user message bubbles.</div>
                  </div>
                  <div style={{ minWidth: 220, flex: '1 1 220px', marginBottom: 24 }}>
                    <div style={{ color: '#6d7175', fontSize: 13, marginBottom: 4 }}>Footer Background Color</div>
                    
<ColorPicker
                      color={footerHSB}
                      onChange={hsb => {
                        setFooterHSB(hsb);
                        const hex = hsbToHex(hsb);
                        setFooterBgColor(hex);
                        setLastValidFooterHex(hex);
                      }}
                      allowAlpha={false}
                    />
                    <div style={{ marginTop: 12 }} />
                    <TextField
                      label=""
                      value={footerBgColor}
                      onChange={val => {
                        setFooterBgColor(val);
                        if (isHexColor(val)) {
                          setLastValidFooterHex(val);
                          setFooterHSB(hexToHsb(val));
                        }
                      }}
                      autoComplete="off"
                      placeholder="#333333"
                    />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Background color of the chat area.</div>
                  </div>
                  <div style={{ minWidth: 220, flex: '1 1 220px', marginBottom: 24 }}>
                    <div style={{ color: '#6d7175', fontSize: 13, marginBottom: 4 }}>Font Color</div>
                    
<ColorPicker
                      color={fontHSB}
                      onChange={hsb => {
                        setFontHSB(hsb);
                        const hex = hsbToHex(hsb);
                        setFontColor(hex);
                        setLastValidFontHex(hex);
                      }}
                      allowAlpha={false}
                    />
                    <div style={{ marginTop: 12 }} />
                    <TextField
                      label=""
                      value={fontColor}
                      onChange={val => {
                        setFontColor(val);
                        if (isHexColor(val)) {
                          setLastValidFontHex(val);
                          setFontHSB(hexToHsb(val));
                        }
                      }}
                      autoComplete="off"
                      placeholder="#000000"
                    />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Font color of the chat widget.</div>
                  </div>
                  <div style={{ minWidth: 220, flex: '1 1 220px', marginBottom: 24 }}>
                    <div style={{ color: '#6d7175', fontSize: 13, marginBottom: 4 }}>Quick Question Pill Background</div>
                    
<ColorPicker
                      color={quickQuestionPillHSB}
                      onChange={hsb => {
                        setQuickQuestionPillHSB(hsb);
                        const hex = hsbToHex(hsb);
                        setQuickQuestionPillBgColor(hex);
                        setLastValidQuickQuestionPillHex(hex);
                      }}
                      allowAlpha={false}
                    />
                    <div style={{ marginTop: 12 }} />
                    <TextField
                      label=""
                      value={quickQuestionPillBgColor}
                      onChange={val => {
                        setQuickQuestionPillBgColor(val);
                        if (isHexColor(val)) {
                          setLastValidQuickQuestionPillHex(val);
                          setQuickQuestionPillHSB(hexToHsb(val));
                        }
                      }}
                      autoComplete="off"
                      placeholder="#000000"
                    />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Background color of the quick question pill.</div>
                  </div>
                  {/* Chat Bubble Color (Open) */}
                  <div style={{ minWidth: 220, flex: '1 1 220px', marginBottom: 24 }}>
                    <div style={{ color: '#6d7175', fontSize: 13, marginBottom: 4 }}>Chat Bubble Color (Open)</div>
                    <ColorPicker
                      color={bubbleOpenHSB}
                      onChange={hsb => {
                        setBubbleOpenHSB(hsb);
                        const hex = hsbToHex(hsb);
                        setChatBubbleOpenColor(hex);
                        setLastValidBubbleOpenHex(hex);
                      }}
                      allowAlpha={false}
                    />
                    <div style={{ marginTop: 12 }} />
                    <TextField
                      label=""
                      value={chatBubbleOpenColor}
                      onChange={val => {
                        setChatBubbleOpenColor(val);
                        if (isHexColor(val)) {
                          setLastValidBubbleOpenHex(val);
                          setBubbleOpenHSB(hexToHsb(val));
                        }
                      }}
                      autoComplete="off"
                      placeholder="#2D2D2D"
                    />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Color of the chat bubble button when open.</div>
                  </div>
                  {/* Chat Bubble Color (Closed) */}
                  <div style={{ minWidth: 220, flex: '1 1 220px', marginBottom: 24 }}>
                    <div style={{ color: '#6d7175', fontSize: 13, marginBottom: 4 }}>Chat Bubble Color (Closed)</div>
                    <ColorPicker
                      color={bubbleClosedHSB}
                      onChange={hsb => {
                        setBubbleClosedHSB(hsb);
                        const hex = hsbToHex(hsb);
                        setChatBubbleClosedColor(hex);
                        setLastValidBubbleClosedHex(hex);
                      }}
                      allowAlpha={false}
                    />
                    <div style={{ marginTop: 12 }} />
                    <TextField
                      label=""
                      value={chatBubbleClosedColor}
                      onChange={val => {
                        setChatBubbleClosedColor(val);
                        if (isHexColor(val)) {
                          setLastValidBubbleClosedHex(val);
                          setBubbleClosedHSB(hexToHsb(val));
                        }
                      }}
                      autoComplete="off"
                      placeholder="#2D2D2D"
                    />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Color of the chat bubble button when closed.</div>
                  </div>
                </div>
                <FormLayout.Group>
                  <Button variant='primary' onClick={handleResetToDefault}>Reset to Default</Button>
                  <Button variant='primary' onClick={handleSave}>Save</Button>
                  <Button variant='primary' onClick={fetchCustomization} style={{marginLeft: 8}}>Reset</Button>
                </FormLayout.Group>
              </FormLayout>
            </Card>
          </div>
          <div style={{ minWidth: 350, maxWidth: 400, flex: '0 0 370px', background: '#fafbfc', border: '1px solid #e1e3e5', borderRadius: 16, padding: 24, boxShadow: '0 2px 8px #0001', marginTop: 8 }}>
            <div style={{ marginTop: 12 }}>
              <AdminChatbotPreview
                open={true}
                shopDomain={new URLSearchParams(window.location.search).get('shop') || ''}
                logoUrl={logoUrl}
                header={header}
                welcomeMessage={welcomeMessage}
                botStatusText={botStatusText}
                headerBgColor={headerBgColor}
                chatBgColor={chatBgColor}
                chatInputBgColor={chatInputBgColor}
                footerBgColor={footerBgColor}
                fontColor={fontColor}
                quickQuestionPillBgColor={quickQuestionPillBgColor}
                botName={botName}
                position={position}
                shape={shape}
                chatBubbleOpenColor={chatBubbleOpenColor}
                chatBubbleClosedColor={chatBubbleClosedColor}
                // Pass quick questions and live chat message as props for preview
                quickQuestions={availableQuickQuestions.filter(q => quickQuestionsVisibility[q.key])}
                liveChatMessage={features.includes('Live chat') ? liveChatMessage : undefined}
                liveChatEnabled={liveChatEnabled}
              />
            </div>
          </div>
        </div>
      </Card>
      )}
      {toastActive && (
        <Toast content="Customization saved!" onDismiss={() => setToastActive(false)} />
      )}
      {toastError && (
        <Toast content={toastError} error onDismiss={() => setToastError('')} />
      )}
    </Frame>
  );
}

// --- HSB/HEX Conversion Utilities ---
function hexToHsb(hex) {
  // Remove # if present
  hex = hex.replace(/^#/, '');
  let r, g, b;
  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
  } else if (hex.length === 6) {
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
  } else {
    r = 0; g = 0; b = 0;
  }
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, v = max;
  const d = max - min;
  s = max === 0 ? 0 : d / max;
  if (max === min) {
    h = 0;
  } else {
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: h = 0;
    }
    h /= 6;
  }
  return {
    hue: Math.round(h * 360),
    saturation: Math.round(s * 100) / 100,
    brightness: Math.round(v * 100) / 100
  };
}
function hsbToHex(hsb) {
  let { hue, saturation, brightness } = hsb;
  hue = hue % 360;
  const c = brightness * saturation;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = brightness - c;
  let r = 0, g = 0, b = 0;
  if (hue < 60) {
    r = c; g = x; b = 0;
  } else if (hue < 120) {
    r = x; g = c; b = 0;
  } else if (hue < 180) {
    r = 0; g = c; b = x;
  } else if (hue < 240) {
    r = 0; g = x; b = c;
  } else if (hue < 300) {
    r = x; g = 0; b = c;
  } else {
    r = c; g = 0; b = x;
  }
  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);
  return (
    '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('')
  );
}
// Helper functions for color conversion
function parseColor(hex) {
  if (typeof hex === 'object' && hex !== null &&
      typeof hex.red === 'number' &&
      typeof hex.green === 'number' &&
      typeof hex.blue === 'number') {
    return hex;
  }
  // Accepts #RRGGBB or #RGB
  if (typeof hex === 'string' && hex[0] === '#') {
    if (hex.length === 7) {
      return {
        red: parseInt(hex.slice(1, 3), 16),
        green: parseInt(hex.slice(3, 5), 16),
        blue: parseInt(hex.slice(5, 7), 16),
      };
    } else if (hex.length === 4) {
      return {
        red: parseInt(hex[1] + hex[1], 16),
        green: parseInt(hex[2] + hex[2], 16),
        blue: parseInt(hex[3] + hex[3], 16),
      };
    }
  }
  return { red: 0, green: 0, blue: 0 };
}

function safeParseColor(val) {
  if (typeof val === 'object' && val !== null &&
      typeof val.red === 'number' &&
      typeof val.green === 'number' &&
      typeof val.blue === 'number') {
    return val;
  }
  return parseColor(val);
}

// Convert HSB (hue, saturation, brightness) to RGB {red, green, blue}
function hsbToRgb({hue, saturation, brightness}) {
  let h = typeof hue === 'number' ? hue : 0;
  let s = typeof saturation === 'number' ? saturation : 0;
  let v = typeof brightness === 'number' ? brightness : 0;

  let r = 0, g = 0, b = 0;
  let i = Math.floor(h / 60) % 6;
  let f = h / 60 - i;
  let p = v * (1 - s);
  let q = v * (1 - f * s);
  let t = v * (1 - (1 - f) * s);

  switch (i) {
    case 0: r = v; g = t; b = p; break;
    case 1: r = q; g = v; b = p; break;
    case 2: r = p; g = v; b = t; break;
    case 3: r = p; g = q; b = v; break;
    case 4: r = t; g = p; b = v; break;
    case 5: r = v; g = p; b = q; break;
  }
  return {
    red: Math.round(r * 255),
    green: Math.round(g * 255),
    blue: Math.round(b * 255)
  };
}

function toHex(color) {
  
  if (!color || typeof color !== 'object') {
    return '#000000';
  }
  // Polaris ColorPicker may provide {red, green, blue}, but also check for nested values or edge cases
  let r = color.red, g = color.green, b = color.blue;
  // Handle if color is {hue, brightness, saturation, alpha} (shouldn't happen, but log if so)
  if (typeof r !== 'number' || typeof g !== 'number' || typeof b !== 'number') {
    // Unexpected color object structure; returning black.
    return '#000000';
  }
  return (
    '#' +
    [r, g, b]
      .map(x => {
        const hex = x.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      })
      .join('')
  );
}

